import { useState, useEffect, useRef, useCallback, useContext } from "react";
import { io } from "socket.io-client";
import { MessageCircle } from "lucide-react";
import { APP_NAME, CHAT_THEME, BRAND } from "../../util/brand";
import axios from "axios";
import customAxios from "../../util/customAxios";
import { UserContext } from "../../context/user";
import { CHAT_OPENING_MESSAGES, CHAT_PLACEHOLDER } from "../../util/chatOpeners";
import { CHAT_OPEN_EVENT, CHAT_RESET_EVENT, getChatSessionId } from "../../util/chatSession";
import "./ChatWidget.css";

const FALLBACK_API_BASE = `${window.location.origin}/api`;
const API_URL = import.meta.env.VITE_API_URL || FALLBACK_API_BASE;
const SOCKET_URL = API_URL.replace(/\/api\/?$/, "");

function isImageUrl(text) {
  return (
    /\.(jpg|jpeg|png|gif|webp)(\?.*)?$/i.test(text) ||
    text.includes("/api/report-screenshots/")
  );
}

const styles = getStyles(CHAT_THEME);
const CHAT_INTAKE_ONLY = Boolean(BRAND.marketing?.chatIntakeOnly);

export default function ChatWidget({ variant = "floating" }) {
  const isSidebar = variant === "sidebar";
  const { user } = useContext(UserContext);
  const canAttachScreenshot = Boolean(user?.token);
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [connected, setConnected] = useState(false);
  const [unread, setUnread] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [pendingImage, setPendingImage] = useState(null);
  const [bugLabel, setBugLabel] = useState("");
  const [imagePreview, setImagePreview] = useState(null);
  const [sessionKey, setSessionKey] = useState(0);
  const socketRef = useRef(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const fileRef = useRef(null);
  const imagePreviewRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, []);

  useEffect(() => {
    imagePreviewRef.current = imagePreview;
  }, [imagePreview]);

  useEffect(() => {
    const onReset = () => {
      setMessages([]);
      setUnread(0);
      setInput("");
      setPendingImage(null);
      if (imagePreviewRef.current) URL.revokeObjectURL(imagePreviewRef.current);
      setImagePreview(null);
      setBugLabel("");
      setSessionKey((k) => k + 1);
    };
    const onOpen = () => setOpen(true);
    window.addEventListener(CHAT_RESET_EVENT, onReset);
    window.addEventListener(CHAT_OPEN_EVENT, onOpen);
    return () => {
      window.removeEventListener(CHAT_RESET_EVENT, onReset);
      window.removeEventListener(CHAT_OPEN_EVENT, onOpen);
    };
  }, []);

  useEffect(() => {
    if (!CHAT_INTAKE_ONLY) return undefined;

    let cancelled = false;
    const sessionId = getChatSessionId();
    axios
      .get(`${API_URL}/basic/chat-history`, { params: { sessionId } })
      .then((res) => {
        if (cancelled) return;
        const history = res.data?.data?.messages;
        if (history?.length) setMessages(history);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [sessionKey]);

  useEffect(() => {
    if (CHAT_INTAKE_ONLY) {
      const sessionId = getChatSessionId();
      const socket = io(SOCKET_URL, {
        query: { sessionId },
        transports: ["polling", "websocket"],
      });

      socket.on("connect", () => {
        setConnected(true);
        socket.emit("chat:visitor_context", { url: window.location.href });
        if (user?.token) {
          socket.emit("chat:auth", { token: user.token });
        }
      });
      socket.on("disconnect", () => setConnected(false));

      socket.on("chat:message", (msg) => {
        setMessages((prev) => {
          const dup = prev.some(
            (m) =>
              m.from === msg.from &&
              m.text === msg.text &&
              Math.abs((m.timestamp || 0) - (msg.timestamp || 0)) < 2000
          );
          if (dup) return prev;
          return [...prev, msg];
        });
        setUnread((u) => u + 1);
      });

      socketRef.current = socket;
      return () => socket.disconnect();
    }

    return undefined;
  }, [user?.token, sessionKey]);

  useEffect(() => {
    if (CHAT_INTAKE_ONLY) return undefined;

    const sessionId = getChatSessionId();
    const socket = io(SOCKET_URL, {
      query: { sessionId },
      transports: ["polling", "websocket"],
    });

    socket.on("connect", () => {
      setConnected(true);
      socket.emit("chat:visitor_context", { url: window.location.href });
      if (user?.token) {
        socket.emit("chat:auth", { token: user.token });
      }
    });
    socket.on("disconnect", () => setConnected(false));

    socket.on("chat:message", (msg) => {
      setMessages((prev) => [...prev, msg]);
      setUnread((prev) => prev + 1);
    });

    socketRef.current = socket;
    return () => socket.disconnect();
  }, [user?.token, sessionKey]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, pendingImage, scrollToBottom]);

  useEffect(() => {
    if (!open || !CHAT_INTAKE_ONLY) return undefined;

    const pollHistory = () => {
      const sessionId = getChatSessionId();
      axios
        .get(`${API_URL}/basic/chat-history`, { params: { sessionId } })
        .then((res) => {
          const history = res.data?.data?.messages;
          if (history?.length) setMessages(history);
        })
        .catch(() => {});
    };

    const interval = setInterval(pollHistory, 10000);
    return () => clearInterval(interval);
  }, [open, sessionKey]);

  useEffect(() => {
    if (!open) return;
    setUnread(0);
    setTimeout(() => inputRef.current?.focus(), 100);
  }, [open]);

  const appendVisitorMessage = (text, label = "") => {
    setMessages((prev) => [
      ...prev,
      {
        text: label ? `[Bug: ${label}]` : text,
        from: "visitor",
        timestamp: Date.now(),
        attachmentUrl: isImageUrl(text) ? text : "",
        bugLabel: label,
      },
    ]);
  };

  const submitIntake = async (text, label = "") => {
    const sessionId = getChatSessionId();
    const headers = {};
    if (user?.token) headers.Authorization = `Bearer ${user.token}`;

    const res = await axios.post(
      `${API_URL}/basic/chat-intake`,
      {
        sessionId,
        message: text,
        bugLabel: label,
        url: window.location.href,
      },
      { headers }
    );

    if (res.data?.confirmation) {
      setMessages((prev) => [
        ...prev,
        {
          text: res.data.confirmation,
          from: "system",
          timestamp: Date.now(),
        },
      ]);
    }
  };

  const emitMessage = async (text, label = "") => {
    if (!text) return;

    appendVisitorMessage(text, label);

    if (CHAT_INTAKE_ONLY) {
      try {
        await submitIntake(text, label);
      } catch (err) {
        console.error("Chat intake failed:", err);
        setMessages((prev) => [
          ...prev,
          {
            text: "Sorry — we couldn't send that. Please try again or email support.",
            from: "system",
            timestamp: Date.now(),
          },
        ]);
      }
      return;
    }

    if (!socketRef.current) return;
    socketRef.current.emit("chat:send", { text, bugLabel: label });
  };

  const sendText = (text) => {
    emitMessage(text);
  };

  const send = () => {
    const text = input.trim();
    if (!text) return;
    sendText(text);
    setInput("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const clearPendingImage = () => {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setPendingImage(null);
    setImagePreview(null);
    setBugLabel("");
  };

  const uploadImage = async (file) => {
    if (!file || !file.type.startsWith("image/")) return;
    if (!canAttachScreenshot) return;
    setPendingImage(file);
    setImagePreview(URL.createObjectURL(file));
    setBugLabel("");
  };

  const sendPendingImage = async () => {
    const label = bugLabel.trim();
    if (!pendingImage || !label) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("image", pendingImage);
      const res = await customAxios.post(`${API_URL}/basic/chat-screenshot`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data?.url) {
        emitMessage(res.data.url, label);
        clearPendingImage();
      }
    } catch (err) {
      console.error("Image upload failed:", err);
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) uploadImage(file);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handlePaste = (e) => {
    if (!canAttachScreenshot) return;
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type.startsWith("image/")) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) uploadImage(file);
        break;
      }
    }
  };

  const renderMessageContent = (msg) => {
    const text = msg.text || "";
    const attachment = msg.attachmentUrl || (isImageUrl(text) ? text : "");
    if (attachment) {
      return (
        <a href={attachment} target="_blank" rel="noopener noreferrer">
          {msg.bugLabel && (
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, opacity: 0.9 }}>
              Bug: {msg.bugLabel}
            </div>
          )}
          <img
            src={attachment}
            alt="Shared screenshot"
            style={{
              maxWidth: "100%",
              maxHeight: 180,
              borderRadius: 8,
              display: "block",
            }}
          />
        </a>
      );
    }
    return text;
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  const headerSubtitle = CHAT_INTAKE_ONLY
    ? "Send a message or screenshot — we'll reply by email"
    : connected
      ? "We typically reply in minutes"
      : "Connecting...";

  const panelStyle = isSidebar ? styles.sidebarPanel : styles.panel;

  return (
    <>
      {!open && isSidebar && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          title="Support"
          aria-label="Open support chat"
          className={`ge-app-sidebar-chat ge-app-sidebar-link ds-nav-item relative ${unread > 0 ? "is-active" : ""}`}
        >
          <MessageCircle className="ge-app-sidebar-icon" size={20} strokeWidth={1.75} />
          <span className="ge-app-sidebar-label ds-nav-label">Support</span>
          {unread > 0 && <span style={styles.sidebarBadge}>{unread > 9 ? "9+" : unread}</span>}
        </button>
      )}

      {!open && !isSidebar && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          style={styles.trigger}
          aria-label="Open chat"
        >
          <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
          </svg>
          {unread > 0 && <span style={styles.badge}>{unread > 9 ? "9+" : unread}</span>}
        </button>
      )}

      {open && (
        <div
          style={panelStyle}
          className={isSidebar ? "wv-chat-panel wv-chat-panel--sidebar" : "wv-chat-panel"}
          role="dialog"
          aria-modal="true"
          aria-labelledby="wv-chat-title"
          aria-describedby="wv-chat-desc"
        >
          <div style={styles.header}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={styles.avatar} aria-hidden="true">
                <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
                </svg>
              </div>
              <div>
                <div id="wv-chat-title" style={styles.headerTitle}>{APP_NAME} support chat</div>
                <div id="wv-chat-desc" style={styles.headerSub}>{headerSubtitle}</div>
              </div>
            </div>
            <button type="button" onClick={() => setOpen(false)} style={styles.closeBtn} aria-label="Close chat">
              <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          <div ref={scrollRef} style={styles.messages} aria-live="polite" aria-relevant="additions">
            {messages.length === 0 && !pendingImage && CHAT_OPENING_MESSAGES.map((text, idx) => (
              <div
                key={idx}
                style={{
                  display: "flex",
                  justifyContent: "flex-start",
                  marginBottom: 8,
                }}
              >
                <div style={{ ...styles.bubble, ...styles.bubbleReply }}>
                  <div style={styles.replyLabel}>{APP_NAME}</div>
                  {text}
                </div>
              </div>
            ))}
            {messages.map((msg, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  justifyContent: msg.from === "visitor" ? "flex-end" : "flex-start",
                  marginBottom: 8,
                }}
              >
                <div
                  style={{
                    ...styles.bubble,
                    ...(msg.from === "visitor" ? styles.bubbleVisitor : styles.bubbleReply),
                  }}
                >
                  {msg.from === "human" && (
                    <div style={styles.replyLabel}>B Ring</div>
                  )}
                  {msg.from === "ai" && (
                    <div style={styles.replyLabel}>AI Assistant</div>
                  )}
                  {msg.from === "system" && (
                    <div style={styles.replyLabel}>{APP_NAME}</div>
                  )}
                  {renderMessageContent(msg)}
                </div>
              </div>
            ))}
            {pendingImage && (
              <div style={{ marginBottom: 8, padding: 10, background: "#f9fafb", borderRadius: 12 }}>
                <img
                  src={imagePreview}
                  alt="Screenshot preview"
                  style={{ maxWidth: "100%", maxHeight: 120, borderRadius: 8, marginBottom: 8 }}
                />
                <input
                  value={bugLabel}
                  onChange={(e) => setBugLabel(e.target.value)}
                  placeholder="Label this bug (required)"
                  className="wv-chat-input"
                  style={{ width: "100%", marginBottom: 8 }}
                />
                <div style={{ display: "flex", gap: 8 }}>
                  <button type="button" onClick={clearPendingImage} style={styles.secondaryBtn}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={!bugLabel.trim() || uploading}
                    onClick={sendPendingImage}
                    style={{
                      ...styles.sendBtn,
                      width: "auto",
                      borderRadius: 8,
                      padding: "8px 14px",
                      opacity: bugLabel.trim() && !uploading ? 1 : 0.4,
                    }}
                  >
                    {uploading ? "Sending…" : "Send screenshot"}
                  </button>
                </div>
              </div>
            )}
            {uploading && !pendingImage && (
              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
                <div style={{ ...styles.bubble, ...styles.bubbleVisitor, opacity: 0.7 }}>
                  Uploading image...
                </div>
              </div>
            )}
          </div>

          {!pendingImage && (
            <div className="wv-chat-input-area" onPaste={handlePaste}>
              {canAttachScreenshot && (
                <>
                  <input
                    type="file"
                    ref={fileRef}
                    onChange={handleFileChange}
                    accept="image/*"
                    style={{ display: "none" }}
                  />
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    style={styles.attachBtn}
                    aria-label="Attach screenshot"
                    disabled={uploading}
                  >
                    <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
                    </svg>
                  </button>
                </>
              )}
              <input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={CHAT_PLACEHOLDER}
                className="wv-chat-input"
                aria-label="Message to support"
              />
              <button
                type="button"
                onClick={send}
                disabled={!input.trim() || uploading}
                style={{
                  ...styles.sendBtn,
                  opacity: input.trim() && !uploading ? 1 : 0.4,
                }}
                aria-label="Send message"
              >
                <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="22" y1="2" x2="11" y2="13" />
                  <polygon points="22 2 15 22 11 13 2 9 22 2" />
                </svg>
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
}

function getStyles(theme) {
  return {
  trigger: {
    position: "fixed",
    bottom: 20,
    left: 20,
    width: 44,
    height: 44,
    borderRadius: "50%",
    background: theme.accent,
    color: "#fff",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: `0 2px 12px ${theme.accentShadow}`,
    zIndex: 9999,
    transition: "transform 0.15s ease",
  },
  badge: {
    position: "absolute",
    top: -2,
    right: -2,
    background: "#ef4444",
    color: "#fff",
    fontSize: 10,
    fontWeight: 700,
    minWidth: 16,
    height: 16,
    padding: "0 4px",
    borderRadius: 999,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  sidebarBadge: {
    position: "absolute",
    top: 2,
    right: 2,
    background: "#ef4444",
    color: "#fff",
    fontSize: 9,
    fontWeight: 700,
    minWidth: 14,
    height: 14,
    padding: "0 3px",
    borderRadius: 999,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: 1,
  },
  panel: {
    position: "fixed",
    bottom: 20,
    left: 20,
    width: 360,
    maxWidth: "calc(100vw - 40px)",
    height: 480,
    maxHeight: "calc(100vh - 40px)",
    borderRadius: 14,
    background: "#fff",
    boxShadow: "0 8px 40px rgba(0,0,0,0.15), 0 2px 8px rgba(0,0,0,0.08)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    zIndex: 9999,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  sidebarPanel: {
    position: "fixed",
    bottom: 20,
    width: 360,
    height: 480,
    maxHeight: "calc(100vh - 40px)",
    borderRadius: 14,
    background: "#fff",
    boxShadow: "0 8px 40px rgba(0,0,0,0.15), 0 2px 8px rgba(0,0,0,0.08)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    zIndex: 9999,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  header: {
    background: theme.accent,
    color: "#fff",
    padding: "16px 16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexShrink: 0,
  },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.2)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  headerTitle: {
    fontWeight: 600,
    fontSize: 15,
  },
  headerSub: {
    fontSize: 12,
    opacity: 0.85,
  },
  closeBtn: {
    background: "none",
    border: "none",
    color: "#fff",
    cursor: "pointer",
    padding: 4,
    opacity: 0.8,
  },
  messages: {
    flex: 1,
    overflowY: "auto",
    padding: 16,
    minHeight: 0,
  },
  welcome: {
    textAlign: "center",
    padding: "32px 16px",
  },
  bubble: {
    maxWidth: "80%",
    padding: "10px 14px",
    borderRadius: 14,
    fontSize: 14,
    lineHeight: 1.45,
    wordBreak: "break-word",
  },
  bubbleVisitor: {
    background: theme.accent,
    color: "#fff",
    borderBottomRightRadius: 4,
  },
  bubbleReply: {
    background: "#f3f4f6",
    color: "#1f2937",
    borderBottomLeftRadius: 4,
  },
  replyLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: "#6b7280",
    marginBottom: 2,
  },
  attachBtn: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    background: "none",
    color: "#6b7280",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    transition: "color 0.15s",
  },

  sendBtn: {
    width: 38,
    height: 38,
    borderRadius: "50%",
    background: theme.accent,
    color: "#fff",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  secondaryBtn: {
    padding: "8px 12px",
    borderRadius: 8,
    border: "1px solid #e5e7eb",
    background: "#fff",
    color: "#374151",
    fontSize: 13,
    cursor: "pointer",
  },
  };
}
